import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isEqual from 'lodash.isequal';

import { RootState } from 'redux/rootReducer';
import useMedia from 'hooks/useMedia';
import { EntityContext } from 'context/entityContext';
import { TopListsContext } from 'context/topLists';

import DSPSelectorLoader from 'components/utility/ContentLoader/DSPSelectorLoader/DSPSelectorLoader';
import { selectSourceOfStreamTypes } from 'redux/reducers/sourceOfStreamTypes';

import {
  shouldLoadSelectedSourceOfStreams,
  loadSelectedSourceOfStreams,
  saveSelectedSourceOfStreams,
} from 'redux/reducers/selectedSourceOfStreams';

import {
  Container,
  Header,
  HeaderContent,
  Label,
  Title,
  Content,
  Footer,
  ResetButton,
  SaveButton,
  Info,
  ResetButtonContainer,
  StyledCloseButton,
  WarningMessage,
} from '../DSPSelector/DSPSelector.style';
import DSPList from '../DSPSelector/DSPList/DSPList';
import SelectedDSPList from '../DSPSelector/SelectedDSPList/SelectedDSPList';
import { ReactComponent as CloseIcon } from 'icons/icon-close-large.svg';
import { withGeneralSettings } from 'components/hocs/withGeneralSettings';

const MinDSPInfo: React.FC = () => (
  <Info>
    <p>
      You can&#39;t delete all Source of Streams from the list. To remove the
      last Source of Stream, add a new one first.
    </p>
  </Info>
);

interface IMaxDSPInfoProps {
  dspCountLimit: number;
}

const MaxDSPInfo: React.FC<IMaxDSPInfoProps> = (props) => {
  const { dspCountLimit } = props;
  return (
    <Info>
      <p>
        This list has reached the limit of {dspCountLimit} Source of Streams. To
        add any new Source of Stream, remove one first.
      </p>
    </Info>
  );
};

const MAX_SELECTED_SOS = 8;

interface IDSPSelectorProps {
  generalSettings: any;
  toggleSelector: any;
  defaultTypes: any;
}

const SourceOfStreamsSelector: React.FC<IDSPSelectorProps> = (props) => {
  const { defaultTypes, toggleSelector } = props;
  const { filters: entityFilters } = useContext(EntityContext);
  const { filters: topListsFilters } = useContext(TopListsContext);
  const streaming = topListsFilters?.streaming || entityFilters?.streaming;

  const [tempSelectedSourceOfStreams, setTempSelectedSourceOfStreams] =
    useState<any[]>();
  const isSmallScreen = useMedia('(min-width: 50em)');
  const dispatch = useDispatch();

  const sourceOfStreams = useSelector((state: RootState) => {
    return selectSourceOfStreamTypes(state, streaming);
  });

  const sourceOfStreamTypes = sourceOfStreams
    .map((type) => ({
      name: type?.id || type,
      id: type?.id || type,
      displayName: type?.displayName || type,
      type: type?.displayName || type,
    }))
    .filter((type: any) => type.name !== 'ALL');

  const selectedSourceOfStreamsRejected = useSelector(
    (state: any) => state.selectedSourceOfStreams.rejected
  );

  useEffect(() => {
    if (sourceOfStreams && sourceOfStreams.length > 0) {
      if (dispatch(shouldLoadSelectedSourceOfStreams())) {
        dispatch(loadSelectedSourceOfStreams());
      }
    }
  }, [dispatch, streaming, sourceOfStreams]);

  const selectedSourceOfStreams = useSelector((state: any) => {
    const selectedSourceOfStreams = state.selectedSourceOfStreams.value;
    if (!selectedSourceOfStreams) return [];
    return selectedSourceOfStreams;
  });

  const selectedSourceOfStreamsIds = selectedSourceOfStreams.map(
    (streams: any) => streams.id
  );

  const defaultSourceOfStreams = defaultTypes || [];

  const selectionLimit = useMemo(() => {
    if (sourceOfStreamTypes) {
      return Math.min(sourceOfStreamTypes.length, MAX_SELECTED_SOS);
    }

    return MAX_SELECTED_SOS;
  }, [sourceOfStreamTypes]);

  const minLimitReached = useMemo(() => {
    if (selectedSourceOfStreams) {
      return selectedSourceOfStreams.length <= 1;
    }

    return false;
  }, [selectedSourceOfStreams]);

  const maxLimitReached = useMemo(() => {
    if (selectedSourceOfStreams) {
      return selectedSourceOfStreams.length >= selectionLimit;
    }

    return false;
  }, [selectedSourceOfStreams, selectionLimit]);

  const showCount = useMemo(() => {
    if (sourceOfStreamTypes) {
      return sourceOfStreamTypes.length > MAX_SELECTED_SOS;
    }

    return true;
  }, [sourceOfStreamTypes]);

  const isDefaultSetting = useMemo(() => {
    return isEqual(selectedSourceOfStreams, defaultSourceOfStreams);
  }, [selectedSourceOfStreams, defaultSourceOfStreams]);

  const resetSourceOfStreams = useCallback(() => {
    dispatch(saveSelectedSourceOfStreams(defaultSourceOfStreams));
  }, [dispatch, defaultSourceOfStreams]);

  const toggleCustomer = useCallback(
    (customer) => {
      // if the customer is already selected
      if (selectedSourceOfStreamsIds.includes(customer.id)) {
        // and we have more than 1 selected customers
        if (!minLimitReached) {
          // remove the customer
          dispatch(
            saveSelectedSourceOfStreams(
              selectedSourceOfStreams.filter((el: any) => el.id !== customer.id)
            )
          );
        }
        // if it is not selected yet
      } else {
        // and we haven't reached the limit
        if (!maxLimitReached) {
          // add the customer]
          dispatch(
            saveSelectedSourceOfStreams([...selectedSourceOfStreams, customer])
          );
        }
      }
    },
    [
      dispatch,
      minLimitReached,
      maxLimitReached,
      selectedSourceOfStreams,
      selectedSourceOfStreamsIds,
    ]
  );

  const saveTempSourceOfStreams = useCallback(() => {
    if (tempSelectedSourceOfStreams) {
      dispatch(saveSelectedSourceOfStreams(tempSelectedSourceOfStreams));
    }
    setTempSelectedSourceOfStreams(undefined);
  }, [dispatch, tempSelectedSourceOfStreams]);

  const changeDSPOrder = useCallback(
    (start, end) => {
      const temp = [
        ...(tempSelectedSourceOfStreams || selectedSourceOfStreams),
      ];
      const customer = temp[start];

      /** Move the item from its old position to the new position by changing the order of the array. */
      temp.splice(start, 1);
      temp.splice(end, 0, customer);

      setTempSelectedSourceOfStreams(temp);
    },
    [selectedSourceOfStreams, tempSelectedSourceOfStreams]
  );

  if (
    !sourceOfStreamTypes ||
    !defaultSourceOfStreams ||
    !selectedSourceOfStreams
  ) {
    return (
      <Container>
        <DSPSelectorLoader />
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderContent>
          <Label>Source of Streams</Label>
          <Title>Customize list</Title>
          {selectedSourceOfStreamsRejected && (
            <WarningMessage>
              Your selected Source of Stream settings failed to load, so
              unfortunately we cannot retrieve or save your personal settings.
              Please try to refresh or{' '}
              <a href="mailto:opus-support@wmg.com">contact Opus Support</a>.
            </WarningMessage>
          )}
          <p>
            {selectionLimit >= MAX_SELECTED_SOS &&
              `Select up to ${MAX_SELECTED_SOS} Source of Streams to visualize in this graph. Drag
          each selected Source up or down to customize the list order. Any change is applied instantly and it
          affects all graphs that show a selection of Source of Streams.`}
            {selectionLimit < MAX_SELECTED_SOS &&
              `Customize the Source of Streams to visualize in this graph. Drag each selected Source up or down to change the list order.
              Any change is applied instantly and it affects all graphs that show a selection of Source of Streams.`}
          </p>
        </HeaderContent>
        {!isSmallScreen && (
          <StyledCloseButton onClick={toggleSelector}>
            <CloseIcon />
          </StyledCloseButton>
        )}
      </Header>
      <Content>
        <div>
          <Label>All Source of Streams</Label>
          <DSPList
            allDSPs={sourceOfStreamTypes}
            selectedDSPs={selectedSourceOfStreams}
            toggleDSP={toggleCustomer}
            dspLimitReached={maxLimitReached}
          />
          <div>
            {minLimitReached && <MinDSPInfo />}
            {maxLimitReached && <MaxDSPInfo dspCountLimit={selectionLimit} />}
          </div>
        </div>
        <div>
          <Label>
            Selected in view
            {showCount &&
              ` (${selectedSourceOfStreams.length} / ${selectionLimit} selected)`}
          </Label>
          <SelectedDSPList
            allDSPs={sourceOfStreamTypes}
            selectedDSPs={
              tempSelectedSourceOfStreams || selectedSourceOfStreams
            }
            toggleDSP={toggleCustomer}
            saveDSPs={saveTempSourceOfStreams}
            changeDSPOrder={changeDSPOrder}
            isSourceOfStreams
          />
          <ResetButtonContainer>
            {isSmallScreen && (
              <ResetButton
                onClick={resetSourceOfStreams}
                disabled={isDefaultSetting}
              >
                Reset to default
              </ResetButton>
            )}
          </ResetButtonContainer>
          {isSmallScreen && (
            <div style={{ marginTop: 'auto' }}>
              <SaveButton onClick={toggleSelector}>Done</SaveButton>
            </div>
          )}
        </div>
      </Content>
      <Footer>
        {!isSmallScreen && (
          <div>
            <ResetButton
              onClick={resetSourceOfStreams}
              disabled={isDefaultSetting}
            >
              Reset to default
            </ResetButton>
          </div>
        )}
        {!isSmallScreen && (
          <div>
            <SaveButton onClick={toggleSelector}>Done</SaveButton>
          </div>
        )}
      </Footer>
    </Container>
  );
};
export default withGeneralSettings(SourceOfStreamsSelector);
